import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Tv,
  Star,
  Play,
  Search,
  Trash2,
  X,
  Layers,
  Sparkles,
  Radio,
  Check,
  Film,
  Zap,
  LayoutGrid,
  List as ListIcon,
  Menu,
  Calendar,
} from 'lucide-react';
import { useIPTV } from '../../context/IPTVContext';
import { Channel } from '../../types/iptv';
import { handleChannelLogoError } from '../../utils/imageUtils';
import { shouldHideCategoryFromList, shouldHideContentFromList } from '../../utils/security';
import { AutoScrollTitle } from '../common/AutoScrollTitle';
import { OpenTvLoader } from '../common/OpenTvLoader';
import { getCurrentProgram, getProgramProgress } from '../../utils/epgService';
import { EmptyCategoryGuide } from '../common/EmptyCategoryGuide';

export const LiveChannelsView: React.FC = () => {
  const {
    channels,
    channelCategories,
    rawChannelCategories,
    categoryConfig,
    activeChannelCategory,
    setActiveChannelCategory,
    currentChannel,
    setCurrentChannel,
    isFavorite,
    toggleFavorite,
    isCategoryFavorite,
    toggleCategoryFavorite,
    currentProfile,
    parentalConfig,
    checkRestricted,
    epgData,
    loadBatchEPG,
    settings,
    updateSettings,
  } = useIPTV();

  // Estado independente para filtrar a lista lateral de categorias (sidebar e drawer)
  const [categoryFilter, setCategoryFilter] = useState('');
  // Estado independente para buscar canais na grade
  const [channelSearch, setChannelSearch] = useState('');
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');
  const [visibleLimit, setVisibleLimit] = useState(80);
  const [isMobileCategoryDrawerOpen, setIsMobileCategoryDrawerOpen] = useState(false);
  const contentAreaRef = useRef<HTMLDivElement>(null);


  // All categories for the left sidebar:
  // Categorias para a grade/barra lateral seguindo RIGOROSAMENTE a ordem definida no Gerenciador / Banco:
  // 1º Favoritos
  // 2º Categorias marcadas como favoritas com a Estrela ⭐ (no topo, logo abaixo de Favoritos)
  // 3º Demais Categorias na sequência EXATA configurada no Gerenciador / Banco
  // E ao desfavoritar, a categoria retorna automaticamente à sua posição original do banco!
  const filteredCategoryCards = useMemo(() => {
    // 1. Filtrar categorias ocultas por perfil / parental
    const visible = channelCategories.filter(
      (cat) => !shouldHideCategoryFromList(cat, currentProfile, parentalConfig)
    );

    const specialPrefix: string[] = [];
    const regularCats: string[] = [];

    visible.forEach((cat) => {
      if (cat === 'Favoritos') {
        specialPrefix.push(cat);
      } else {
        regularCats.push(cat);
      }
    });

    // Separar categorias normais entre favoritadas com ⭐ e não favoritadas
    const starredCats: string[] = [];
    const unstarredCats: string[] = [];

    regularCats.forEach((cat) => {
      if (isCategoryFavorite('live', cat)) {
        starredCats.push(cat);
      } else {
        unstarredCats.push(cat);
      }
    });

    let fullList = [...specialPrefix, ...starredCats, ...unstarredCats];
    if (categoryFilter.trim()) {
      const q = categoryFilter.toLowerCase().trim();
      fullList = fullList.filter(
        (cat) => cat === 'Favoritos' || cat.toLowerCase().includes(q)
      );
    }
    return fullList;
  }, [channelCategories, currentProfile, parentalConfig, isCategoryFavorite, categoryFilter]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    const liveRenames = categoryConfig.live?.renames || {};
    for (const cat of filteredCategoryCards) {
      if (cat === 'Favoritos') {
        counts[cat] = channels.filter((c) => isFavorite(c.id)).length;
      } else {
        counts[cat] = channels.filter((c) => {
          const chCat = c.groupTitle || 'Geral';
          const chCatDisplay = liveRenames[chCat] || chCat;
          return chCat === cat || chCatDisplay === cat;
        }).length;
      }
    }
    return counts;
  }, [filteredCategoryCards, channels, isFavorite, categoryConfig.live?.renames]);


  // Channel list when inside a selected category or global search across all categories (Ordenação Automática A-Z)
  const categoryChannels = useMemo(() => {
    const isSearching = !!channelSearch.trim();

    const liveHidden = new Set(categoryConfig?.live?.hidden || []);
    const renames = categoryConfig?.live?.renames || {};

    const list = channels.filter((ch) => {
      if (ch.hidden) return false;
      if (shouldHideContentFromList(ch, currentProfile, parentalConfig)) return false;

      // 1. Ocultar canais cujas categorias estejam ocultas no Gerenciador ou Controle Parental
      const chCat = ch.groupTitle || 'Geral';
      const rename = renames[chCat];
      if (liveHidden.has(chCat) || (rename && liveHidden.has(rename))) return false;
      if (shouldHideCategoryFromList(chCat, currentProfile, parentalConfig)) return false;
      if (rename && shouldHideCategoryFromList(rename, currentProfile, parentalConfig)) return false;

      if (isSearching) {
        const q = channelSearch.toLowerCase().trim();
        const matchName = ch.name.toLowerCase().includes(q);
        const matchNum = ch.num.toString().includes(q);
        const matchGroup = chCat.toLowerCase().includes(q) || (rename && rename.toLowerCase().includes(q));
        return matchName || matchNum || matchGroup;
      }

      if (activeChannelCategory === 'Favoritos') {
        if (!isFavorite(ch.id)) return false;
      } else if (activeChannelCategory) {
        if (chCat !== activeChannelCategory && rename !== activeChannelCategory) {
          return false;
        }
      }

      return true;
    });

    // Ordenar canais ao vivo respeitando o número/ordem do gerenciador (ou fallback A-Z se mesmo número)
    return [...list].sort((a, b) => {
      const numA = (typeof a.num === 'number' && a.num > 0) ? a.num : 99999;
      const numB = (typeof b.num === 'number' && b.num > 0) ? b.num : 99999;
      if (numA !== numB) return numA - numB;
      return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base', numeric: true });
    });
  }, [
    channels,
    activeChannelCategory,
    categoryConfig?.live,
    channelSearch,
    isFavorite,
    currentProfile,
    parentalConfig,
  ]);

  // Reset pagination limit, scroll to top when category or channelSearch changes
  React.useEffect(() => {
    setVisibleLimit(80);
    if (contentAreaRef.current) {
      contentAreaRef.current.scrollTop = 0;
    }
  }, [activeChannelCategory, channelSearch]);

  // Focus first item ONLY when changing category via remote/keyboard (never while typing in an input)
  React.useEffect(() => {
    const isTyping = document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA';
    if (isTyping) return;

    const timer = setTimeout(() => {
      if (document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        const firstCard = contentAreaRef.current?.querySelector<HTMLElement>('[data-channel-card="true"], [tabindex="0"]');
        firstCard?.focus();
      }
    }, 60);
    return () => clearTimeout(timer);
  }, [activeChannelCategory]);

  const displayedChannels = useMemo(() => {
    return categoryChannels.slice(0, visibleLimit);
  }, [categoryChannels, visibleLimit]);

  // Carregamento inteligente e leve da programação EPG em lote para os canais visíveis da categoria
  useEffect(() => {
    if (settings.showGridEpg === false || displayedChannels.length === 0) return;
    const timer = setTimeout(() => {
      loadBatchEPG(displayedChannels.slice(0, 40));
    }, 350);
    return () => clearTimeout(timer);
  }, [displayedChannels, activeChannelCategory, settings.showGridEpg, loadBatchEPG]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 450) {
      if (displayedChannels.length < categoryChannels.length) {
        setVisibleLimit((prev) => prev + 80);
      }
    }
  };

  const getChannelProgs = useCallback((channel: Channel) => {
    if (!channel) return undefined;
    if (epgData && channel.id && epgData[channel.id]?.length) return epgData[channel.id];
    if (epgData && channel.epgId && epgData[channel.epgId]?.length) return epgData[channel.epgId];
    if (channel.qualities && Array.isArray(channel.qualities)) {
      for (const q of channel.qualities) {
        if (!q || !q.streamUrl || typeof q.streamUrl !== 'string') continue;
        const m = q.streamUrl.match(/(?:\/live\/[^\/]+\/[^\/]+\/|\/)([0-9]+)(?:\.m3u8|\.ts|\.mp4|\/|$)/i);
        if (m && epgData) {
          if (epgData[`ch-xtream-${m[1]}`]?.length) return epgData[`ch-xtream-${m[1]}`];
          if (epgData[m[1]]?.length) return epgData[m[1]];
        }
      }
    }
    return undefined;
  }, [epgData]);

  return (
    <div className="w-full h-full flex bg-zinc-950 overflow-hidden select-none">
      {/* LEFT SIDEBAR: Desktop / Tablet */}
      <div className="hidden md:flex md:w-72 lg:w-80 shrink-0 bg-zinc-950 border-r border-zinc-800/80 flex-col h-full overflow-hidden">
        {/* Sidebar Header & Search */}
        <div className="p-4 border-b border-zinc-800/80 shrink-0 bg-zinc-900/40">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs md:text-sm font-black text-white flex items-center gap-2">
              <Layers className="w-4 h-4 text-theme-accent" />
              <span>Categorias ({channelCategories.length})</span>
            </h2>
          </div>

          <div className="relative">
            <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder="Filtrar categorias..."
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full pl-9 pr-8 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-theme-accent"
            />
            {categoryFilter && (
              <button
                type="button"
                onClick={() => setCategoryFilter('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-5 h-5 rounded-full flex items-center justify-center text-zinc-400 hover:text-white bg-zinc-800/90 hover:bg-zinc-700 transition cursor-pointer shadow-sm"
                title="Limpar filtro de categorias"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Categories Vertical List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-thin scrollbar-thumb-zinc-800">
          {filteredCategoryCards.map((category) => {
            const isSelected = activeChannelCategory === category;
            const isFav = category === 'Favoritos';
            const isCategoryFav = !isFav && isCategoryFavorite('live', category);

            const count = isFav
              ? channels.filter((c) => isFavorite(c.id)).length
              : channels.filter((c) => {
                  const rename = categoryConfig.live.renames[c.groupTitle];
                  return c.groupTitle === category || rename === category;
                }).length;

            return (
              <div
                key={category}
                className={`w-full group/cat relative flex items-center rounded-xl transition cursor-pointer ${
                  isSelected
                    ? 'bg-theme-accent text-black font-black shadow-md shadow-theme-accent-sm'
                    : isCategoryFav
                    ? 'bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/30'
                    : 'bg-zinc-900/40 hover:bg-zinc-900 text-zinc-300 hover:text-white border border-transparent hover:border-zinc-800'
                }`}
              >
                <button
                  onClick={() => {
                    setActiveChannelCategory(category);
                    setChannelSearch('');
                    setCategoryFilter('');
                  }}
                  className="flex-1 text-left px-3 py-2.5 text-xs font-bold flex items-center justify-between gap-2 min-w-0 cursor-pointer"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    {isFav ? (
                      <Star
                        className={`w-3.5 h-3.5 shrink-0 ${
                          isSelected ? 'fill-black' : 'text-amber-400 fill-amber-400'
                        }`}
                      />
                    ) : isCategoryFav ? (
                      <Star
                        className={`w-3.5 h-3.5 shrink-0 fill-amber-400 ${
                          isSelected ? 'text-black fill-black' : 'text-amber-400'
                        }`}
                      />
                    ) : (
                      <Tv
                        className={`w-3.5 h-3.5 shrink-0 ${
                          isSelected ? 'text-black' : 'text-zinc-500 group-hover/cat:text-theme-accent'
                        }`}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <AutoScrollTitle
                        text={category}
                        className={isSelected ? 'text-black font-black' : ''}
                      />
                    </div>
                  </div>
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full font-mono shrink-0 ${
                      isSelected
                        ? 'bg-black/20 text-black font-black'
                        : isCategoryFav
                        ? 'bg-amber-500/20 text-amber-300 font-bold'
                        : 'bg-zinc-800 text-zinc-400'
                    }`}
                  >
                    {count}
                  </span>
                </button>

                {/* Star / Favorite Toggle Button for Category */}
                {!isFav && (
                  <button
                    type="button"
                    title={isCategoryFav ? 'Remover categoria dos favoritos' : 'Favoritar categoria'}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCategoryFavorite('live', category);
                    }}
                    className={`pr-2.5 pl-1 py-2 transition cursor-pointer ${
                      isSelected
                        ? 'text-black hover:opacity-75'
                        : isCategoryFav
                        ? 'text-amber-400 opacity-100 hover:scale-125'
                        : 'text-zinc-500 opacity-0 group-hover/cat:opacity-100 hover:text-amber-400 hover:scale-125'
                    }`}
                  >
                    <Star
                      className={`w-3.5 h-3.5 ${
                        isCategoryFav
                          ? isSelected
                            ? 'fill-black'
                            : 'fill-amber-400'
                          : ''
                      }`}
                    />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* CENTER/RIGHT PANEL: Channels in Card or List view */}
      <div className="flex-1 flex flex-col bg-zinc-950 h-full overflow-hidden">
        {/* Mobile Header with 3 pausinhos (Menu) to open vertical categories */}
        <div className="md:hidden p-3 border-b border-zinc-800/80 bg-zinc-900/70 flex items-center justify-between gap-2.5 shrink-0">
          <button
            type="button"
            onClick={() => setIsMobileCategoryDrawerOpen(true)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl font-bold text-xs cursor-pointer shrink-0 active:scale-95 transition bg-theme-accent-subtle border border-theme-accent-border text-theme-accent-light shadow-theme-accent-sm ring-1 ring-theme-accent hover:opacity-90 ${
              displayedChannels.length === 0 ? 'animate-pulse' : ''
            }`}
            title="Escolha a categoria"
          >
            <Menu className="w-4 h-4 text-theme-accent" />
            <span>Escolha a categoria</span>
          </button>

          <div className="relative flex-1 min-w-0">
            <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder="Pesquisar canal..."
              value={channelSearch}
              onChange={(e) => {
                setChannelSearch(e.target.value);
                setIsMobileCategoryDrawerOpen(false);
              }}
              className="w-full pl-9 pr-8 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-theme-accent"
            />
            {channelSearch && (
              <button
                type="button"
                onClick={() => setChannelSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-5 h-5 rounded-full flex items-center justify-center text-zinc-400 hover:text-white bg-zinc-800/90 hover:bg-zinc-700 transition cursor-pointer shadow-sm"
                title="Limpar busca de canais"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Mobile Vertical Drawer (Menu vertical um abaixo do outro) */}
        {isMobileCategoryDrawerOpen && (
          <div className="fixed inset-0 z-50 flex bg-black/80 backdrop-blur-sm md:hidden animate-in fade-in duration-200">
            <div className="w-80 max-w-[85vw] bg-zinc-950 border-r border-zinc-800 h-full flex flex-col shadow-2xl animate-in slide-in-from-left duration-200">
              {/* Drawer Header */}
              <div className="p-4 border-b border-zinc-800 flex items-center justify-between bg-zinc-900/60">
                <h3 className="text-sm font-black text-white flex items-center gap-2">
                  <Layers className="w-4 h-4 text-theme-accent" />
                  <span>Categorias ({channelCategories.length})</span>
                </h3>
                <button
                  onClick={() => setIsMobileCategoryDrawerOpen(false)}
                  className="p-1.5 rounded-xl text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Drawer Search (Filtro exclusivo da lista de categorias) */}
              <div className="p-3 border-b border-zinc-800 bg-zinc-900/40">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Filtrar categorias..."
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full pl-9 pr-8 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-theme-accent"
                  />
                  {categoryFilter && (
                    <button
                      type="button"
                      onClick={() => setCategoryFilter('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-5 h-5 rounded-full flex items-center justify-center text-zinc-400 hover:text-white bg-zinc-800/90 hover:bg-zinc-700 transition cursor-pointer shadow-sm"
                      title="Limpar filtro"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>

              {/* Vertical List one below the other */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-thin scrollbar-thumb-zinc-800">
                {filteredCategoryCards.map((category) => {
                  const isSelected = activeChannelCategory === category;
                  const isFav = category === 'Favoritos';
                  const isCategoryFav = !isFav && isCategoryFavorite('live', category);
                  const count = isFav
                    ? channels.filter((c) => isFavorite(c.id)).length
                    : channels.filter((c) => {
                        const rename = categoryConfig.live.renames[c.groupTitle];
                        return c.groupTitle === category || rename === category;
                      }).length;

                  return (
                    <div
                      key={category}
                      className={`w-full flex items-center rounded-xl transition cursor-pointer ${
                        isSelected
                          ? 'bg-theme-accent text-black font-black shadow-md shadow-theme-accent-sm'
                          : isCategoryFav
                          ? 'bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/30'
                          : 'bg-zinc-900/40 hover:bg-zinc-900 text-zinc-300 hover:text-white border border-transparent hover:border-zinc-800'
                      }`}
                    >
                      <button
                        onClick={() => {
                          setActiveChannelCategory(category);
                          setChannelSearch('');
                          setCategoryFilter('');
                          setIsMobileCategoryDrawerOpen(false);
                        }}
                        className="flex-1 text-left px-3.5 py-3 text-xs font-bold flex items-center justify-between gap-2 min-w-0 cursor-pointer"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          {isFav ? (
                            <Star className={`w-4 h-4 shrink-0 ${isSelected ? 'fill-black' : 'text-amber-400 fill-amber-400'}`} />
                          ) : isCategoryFav ? (
                            <Star className={`w-4 h-4 shrink-0 fill-amber-400 ${isSelected ? 'text-black fill-black' : 'text-amber-400'}`} />
                          ) : (
                            <Tv className={`w-4 h-4 shrink-0 ${isSelected ? 'text-black' : 'text-zinc-500'}`} />
                          )}
                          <span className="truncate">{category}</span>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono shrink-0 ${isSelected ? 'bg-black/20 text-black font-black' : 'bg-zinc-800 text-zinc-400'}`}>
                          {count}
                        </span>
                      </button>

                      {/* Botão da Estrela no Drawer Mobile */}
                      {!isFav && (
                        <button
                          type="button"
                          title={isCategoryFav ? 'Remover categoria dos favoritos' : 'Favoritar categoria'}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCategoryFavorite('live', category);
                          }}
                          className={`pr-3 pl-1 py-3 transition cursor-pointer shrink-0 ${
                            isSelected
                              ? 'text-black hover:opacity-75'
                              : isCategoryFav
                              ? 'text-amber-400 opacity-100 hover:scale-125'
                              : 'text-zinc-500 opacity-60 hover:opacity-100 hover:text-amber-400 hover:scale-125'
                          }`}
                        >
                          <Star
                            className={`w-4 h-4 ${
                              isCategoryFav
                                ? isSelected
                                  ? 'fill-black'
                                  : 'fill-amber-400'
                                : ''
                            }`}
                          />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex-1 cursor-pointer" onClick={() => setIsMobileCategoryDrawerOpen(false)} />
          </div>
        )}

        {/* Top Control Bar */}
        <div className="p-3 sm:p-4 md:p-6 border-b border-zinc-800/80 bg-zinc-900/40 flex items-center justify-between gap-2.5 shrink-0">
          <div className="min-w-0 flex-1 flex items-center gap-2">
            <h1 className="text-sm sm:text-base md:text-xl font-black text-white truncate">
              {channelSearch.trim()
                ? `Busca: "${channelSearch.trim()}" (${categoryChannels.length} ${categoryChannels.length === 1 ? 'canal' : 'canais'})`
                : (activeChannelCategory || 'Favoritos')}
            </h1>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {/* Campo de Busca de Canais no Desktop (Top Bar) */}
            <div className="hidden md:flex relative w-44 lg:w-60">
              <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                placeholder="Pesquisar canal..."
                value={channelSearch}
                onChange={(e) => setChannelSearch(e.target.value)}
                className="w-full pl-9 pr-8 py-1.5 bg-zinc-900 border border-zinc-800 rounded-xl text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-theme-accent transition"
              />
              {channelSearch && (
                <button
                  type="button"
                  onClick={() => setChannelSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-5 h-5 rounded-full flex items-center justify-center text-zinc-400 hover:text-white bg-zinc-800/90 hover:bg-zinc-700 transition cursor-pointer shadow-sm"
                  title="Limpar busca de canais"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>

            {/* Toggle Favorite on Active Category */}
            {activeChannelCategory && activeChannelCategory !== 'Favoritos' && (
              <button
                onClick={() => toggleCategoryFavorite('live', activeChannelCategory)}
                className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl border text-xs font-bold transition cursor-pointer ${
                  isCategoryFavorite('live', activeChannelCategory)
                    ? 'bg-amber-500/15 border-amber-500/40 text-amber-400 shadow-sm'
                    : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700'
                }`}
                title={isCategoryFavorite('live', activeChannelCategory) ? 'Remover dos favoritos' : 'Favoritar Categoria'}
              >
                <Star
                  className={`w-3.5 h-3.5 shrink-0 ${
                    isCategoryFavorite('live', activeChannelCategory)
                      ? 'fill-amber-400 text-amber-400'
                      : ''
                  }`}
                />
                <span className="hidden sm:inline">
                  {isCategoryFavorite('live', activeChannelCategory)
                    ? 'Favorita'
                    : 'Favoritar Categoria'}
                </span>
              </button>
            )}

            {/* Toggle Guia EPG na Grade */}
            <button
              onClick={() => updateSettings({ showGridEpg: settings.showGridEpg === false ? true : false })}
              className={`px-2.5 py-1.5 rounded-xl border text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shrink-0 ${
                settings.showGridEpg !== false
                  ? 'bg-theme-accent-subtle text-theme-accent-light border-theme-accent-border hover:opacity-90 shadow-sm'
                  : 'bg-zinc-900/80 text-zinc-500 border-zinc-800 hover:text-zinc-300 hover:bg-zinc-800'
              }`}
              title={settings.showGridEpg !== false ? 'Guia de Programação na Grade Ativado (Clique para ocultar)' : 'Guia de Programação na Grade Desativado (Clique para exibir)'}
            >
              <Calendar className="w-3.5 h-3.5" />
              <span className="hidden md:inline">
                {settings.showGridEpg !== false ? 'Guia Ativo' : 'Guia Oculto'}
              </span>
            </button>

            {/* View Mode Toggle (Cards vs List) */}
            <div className="flex items-center bg-zinc-900 p-0.5 rounded-xl border border-zinc-800 shrink-0">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-1.5 sm:p-2 rounded-lg text-xs font-bold transition cursor-pointer ${
                  viewMode === 'cards'
                    ? 'bg-theme-accent text-black shadow-sm'
                    : 'text-zinc-400 hover:text-white'
                }`}
                title="Visualização em Cards"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 sm:p-2 rounded-lg text-xs font-bold transition cursor-pointer ${
                  viewMode === 'list'
                    ? 'bg-theme-accent text-black shadow-sm'
                    : 'text-zinc-400 hover:text-white'
                }`}
                title="Visualização em Lista"
              >
                <ListIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Channels Content Area */}
        <div
          ref={contentAreaRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-4 md:p-6 scrollbar-thin scrollbar-thumb-zinc-800"
        >
          {displayedChannels.length === 0 ? (
            channels.length === 0 ? (
              <div className="py-20 text-center max-w-md mx-auto px-4">
                <Tv className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">
                  Nenhuma Lista de Canais Cadastrada
                </h3>
                <p className="text-xs text-zinc-400 mb-4 leading-relaxed">
                  Para começar a assistir TV ao vivo, acesse o menu de Configurações (ícone de engrenagem) e conecte sua lista IPTV própria via URL M3U ou Xtream Codes API.
                </p>
              </div>
            ) : (
              <EmptyCategoryGuide
                type="live"
                categoryName={activeChannelCategory || 'Favoritos'}
                availableCategories={filteredCategoryCards}
                onSelectCategory={(cat) => {
                  setActiveChannelCategory(cat);
                  setChannelSearch('');
                  setCategoryFilter('');
                }}
                onOpenMobileDrawer={() => setIsMobileCategoryDrawerOpen(true)}
                categoryCounts={categoryCounts}
                totalItemsCount={channels.length}
              />
            )
          ) : viewMode === 'cards' ? (
            /* CARDS GRID VIEW */
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {displayedChannels.map((channel) => {
                const isPlayingThis = currentChannel?.id === channel.id;
                const fav = isFavorite(channel.id);

                return (
                  <div
                    key={channel.id}
                    data-channel-card="true"
                    tabIndex={0}
                    onClick={() => {
                      setCurrentChannel(channel);
                      setChannelSearch('');
                      setCategoryFilter('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setCurrentChannel(channel);
                        setChannelSearch('');
                        setCategoryFilter('');
                      } else if (e.key === ' ') {
                        e.preventDefault();
                        toggleFavorite(channel.id);
                      }
                    }}
                    className={`group relative p-3 rounded-2xl border transition-all duration-200 cursor-pointer flex flex-col justify-between select-none focus:outline-none virtual-card-contain ${
                      isPlayingThis
                        ? 'bg-theme-accent-subtle border-theme-accent shadow-xl shadow-theme-accent-sm scale-105 ring-2 ring-theme-accent'
                        : 'bg-zinc-900/70 border-zinc-800/90 hover:border-zinc-600 hover:bg-zinc-800/90'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] font-mono font-bold text-zinc-400 px-1.5 py-0.5 rounded-md bg-black/40">
                        #{channel.num}
                      </span>
                      <div className="flex items-center gap-1">
                        {channel.qualities && channel.qualities.length > 1 ? (
                          <span
                            className="text-[9px] font-mono font-black px-1.5 py-0.5 rounded bg-theme-accent-subtle text-theme-accent border border-theme-accent-subtle"
                            title={`Qualidades disponíveis: ${channel.qualities.map((q) => q.resolution).join(', ')}`}
                          >
                            {channel.qualities.some((q) => q.resolution === '4K') ? '4K • MULTI' : 'MULTI'}
                          </span>
                        ) : channel.resolution === '4K' ? (
                          <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-theme-accent text-black">
                            4K
                          </span>
                        ) : null}
                        <div
                          aria-hidden="true"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(channel.id);
                          }}
                          className="p-1 text-zinc-500 hover:text-amber-400 transition cursor-pointer"
                          title="Favoritar"
                        >
                          <Star
                            className={`w-3.5 h-3.5 ${
                              fav ? 'fill-amber-400 text-amber-400' : ''
                            }`}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="w-full aspect-video rounded-xl bg-black/60 border border-zinc-800/80 p-2 flex items-center justify-center overflow-hidden mb-2 relative group-hover:scale-[1.02] transition">
                      <img
                        src={channel.logo || ''}
                        data-original-src={channel.logo || ''}
                        alt={channel.name}
                        loading="lazy"
                        className="max-w-full max-h-full object-contain"
                        referrerPolicy="no-referrer"
                        onError={(e) => handleChannelLogoError(e, channel.name, channel.num)}
                      />
                      {isPlayingThis && (
                        <div className="absolute inset-0 bg-theme-accent-subtle backdrop-blur-[1px] flex items-center justify-center">
                          <div className="w-8 h-8 rounded-full bg-theme-accent text-black flex items-center justify-center font-black shadow-lg">
                            <Play className="w-4 h-4 fill-current ml-0.5" />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="min-w-0">
                      <AutoScrollTitle
                        text={channel.name}
                        className="text-xs font-bold text-white group-hover:text-theme-accent-light transition"
                      />
                      {(() => {
                        if (settings.showGridEpg === false) {
                          return (
                            <p className="text-[10px] truncate mt-0.5 text-zinc-400">
                              {channel.groupTitle}
                            </p>
                          );
                        }
                        const progs = getChannelProgs(channel);
                        const liveProg = progs ? getCurrentProgram(progs) : null;
                        const progress = liveProg ? getProgramProgress(liveProg) : 0;
                        return (
                          <div className="mt-0.5 space-y-1">
                            {liveProg ? (
                              <AutoScrollTitle
                                text={`${new Date(liveProg.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ${liveProg.title}`}
                                className="text-[10px] font-medium text-theme-accent-light"
                                alwaysScroll={true}
                              >
                                <span className="text-zinc-400 font-mono mr-1.5">
                                  {new Date(liveProg.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                <span className="text-theme-accent-light font-medium">{liveProg.title}</span>
                              </AutoScrollTitle>
                            ) : (
                              <p className="text-[10px] truncate text-zinc-400">
                                {channel.groupTitle}
                              </p>
                            )}
                            {liveProg && (
                              <div className="w-full bg-zinc-800 rounded-full h-1 overflow-hidden">
                                <div
                                  className="bg-theme-accent h-full rounded-full transition-all duration-300"
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* LIST VIEW (TABLE) */
            <div className="space-y-1.5">
              {displayedChannels.map((channel) => {
                const isPlayingThis = currentChannel?.id === channel.id;
                const fav = isFavorite(channel.id);

                return (
                  <div
                    key={channel.id}
                    tabIndex={0}
                    onClick={() => {
                      setCurrentChannel(channel);
                      setChannelSearch('');
                      setCategoryFilter('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setCurrentChannel(channel);
                        setChannelSearch('');
                        setCategoryFilter('');
                      } else if (e.key === ' ') {
                        e.preventDefault();
                        toggleFavorite(channel.id);
                      }
                    }}
                    className={`group p-3 rounded-2xl border transition flex items-center justify-between gap-3 cursor-pointer focus:outline-none virtual-channel-contain ${
                      isPlayingThis
                        ? 'bg-theme-accent-subtle border-theme-accent shadow-md ring-2 ring-theme-accent'
                        : 'bg-zinc-900/60 border-zinc-800/80 hover:bg-zinc-900 hover:border-zinc-700'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xs font-mono font-bold text-zinc-400 w-10 text-center py-1 rounded bg-black/40">
                        #{channel.num}
                      </span>
                      <div className="w-10 h-10 rounded-xl bg-black/60 border border-zinc-800 p-1 flex items-center justify-center overflow-hidden shrink-0">
                        <img
                          src={channel.logo || ''}
                          data-original-src={channel.logo || ''}
                          alt={channel.name}
                          loading="lazy"
                          className="w-full h-full object-contain"
                          referrerPolicy="no-referrer"
                          onError={(e) => handleChannelLogoError(e, channel.name, channel.num)}
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <AutoScrollTitle
                            text={channel.name}
                            className="text-xs md:text-sm font-bold text-white"
                          />
                          {channel.qualities && channel.qualities.length > 1 ? (
                            <span
                              className="text-[9px] font-mono font-black px-1.5 py-0.5 rounded bg-theme-accent-subtle text-theme-accent border border-theme-accent-subtle shrink-0"
                              title={`Qualidades: ${channel.qualities.map((q) => q.resolution).join(', ')}`}
                            >
                              {channel.qualities.some((q) => q.resolution === '4K') ? '4K • MULTI' : 'MULTI'}
                            </span>
                          ) : channel.resolution === '4K' ? (
                            <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-theme-accent text-black shrink-0">
                              4K
                            </span>
                          ) : null}
                        </div>
                        {(() => {
                          if (settings.showGridEpg === false) {
                            return (
                              <p className="text-[11px] truncate mt-0.5 text-zinc-400">
                                {channel.groupTitle}
                              </p>
                            );
                          }
                          const progs = getChannelProgs(channel);
                          const liveProg = progs ? getCurrentProgram(progs) : null;
                          const progress = liveProg ? getProgramProgress(liveProg) : 0;
                          return (
                            <div className="mt-0.5 space-y-1">
                              {liveProg ? (
                                <AutoScrollTitle
                                  text={`${new Date(liveProg.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ${liveProg.title}`}
                                  className="text-[11px] font-medium text-theme-accent-light"
                                  alwaysScroll={true}
                                >
                                  <span className="text-zinc-400 font-mono mr-1.5">
                                    {new Date(liveProg.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                  <span className="text-theme-accent-light font-medium">{liveProg.title}</span>
                                </AutoScrollTitle>
                              ) : (
                                <p className="text-[11px] truncate text-zinc-400">
                                  {channel.groupTitle}
                                </p>
                              )}
                              {liveProg && (
                                <div className="w-full bg-zinc-800 rounded-full h-1 overflow-hidden max-w-xs">
                                  <div
                                    className="bg-theme-accent h-full rounded-full transition-all duration-300"
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <div
                        aria-hidden="true"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(channel.id);
                        }}
                        className="p-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-amber-400 transition cursor-pointer"
                        title="Favoritar"
                      >
                        <Star
                          className={`w-4 h-4 ${
                            fav ? 'fill-amber-400 text-amber-400' : ''
                          }`}
                        />
                      </div>

                      <button
                        onClick={() => setCurrentChannel(channel)}
                        className={`px-3 py-2 rounded-xl text-xs font-black flex items-center gap-1.5 transition ${
                          isPlayingThis
                            ? 'bg-theme-accent text-black'
                            : 'bg-zinc-800 hover:bg-zinc-700 text-white'
                        }`}
                      >
                        <Play className="w-3.5 h-3.5 fill-current" />
                        <span>Assistir</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {categoryChannels.length > visibleLimit && (
            <div className="pt-6 pb-6 flex flex-col items-center justify-center gap-2 opacity-80">
              <OpenTvLoader size="compact" glow="none" />
              <span className="text-[11px] text-zinc-400 font-medium">
                Carregando mais canais... ({displayedChannels.length} de {categoryChannels.length})
              </span>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
